import {
  ConfigureAutoPay,
  MakePayment,
  ManageBankConnections,
  Statements,
} from "@capitalos/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type } from "arktype";
import { Calendar, CreditCard, EllipsisVertical, Settings } from "lucide-react";
import type * as React from "react";
import { formatCurrency } from "shared";
import {
  AccountActionsLoading,
  AccountActionWrapper,
} from "@/components/account-action-wrapper";
import { Page } from "@/components/page";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { RenderCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { restrictRoute } from "@/lib/routeBlocker";
import type { RouterOutputs } from "@/lib/trpcClient";
import { useTRPC } from "@/lib/trpcClient";

const validateSearch = type({
  tab: "'payment-history' | 'statements'='payment-history'",
  "view?": "'make-payment' | 'autopay-settings' | 'bank-connections'",
});
export const Route = createFileRoute("/_authed/_org/organization/statements")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/organization/people",
      },
      requiredRole: "bookkeeper",
    }),
  component: RouteComponent,
  validateSearch,
});

type StatementPayment =
  RouterOutputs["transaction"]["listStatementPayments"][number];

const tableColumns: ColumnDef<StatementPayment>[] = [
  {
    accessorKey: "originatedAt",
    cell: ({ row }) => (
      <RenderCell type="date" date={row.original.originatedAt} />
    ),
    header: "Date",
  },
  {
    accessorKey: "amountCents",
    cell: ({ row }) => (
      <RenderCell type="currency" amount={Math.abs(row.original.amountCents)} />
    ),
    header: "Amount",
  },
  {
    accessorKey: "type",
    cell: ({ row }) => <span className="capitalize">{row.original.type}</span>,
    header: "Type",
  },
  {
    accessorKey: "status",
    cell: ({ row }) => (
      <RenderStatusBadge status={row.original.status as any} />
    ),
    header: "Status",
  },
  {
    accessorKey: "settledAt",
    cell: ({ row }) =>
      row.original.settledAt ? (
        <RenderCell type="date" date={row.original.settledAt} />
      ) : (
        "-"
      ),
    header: "Settled Date",
  },
];

interface StatementHeaderProps {
  onOpenMakePayment: () => void;
  onOpenAutopaySettings: () => void;
  onOpenBankConnections: () => void;
}

function StatementHeader({
  onOpenMakePayment,
  onOpenAutopaySettings,
  onOpenBankConnections,
}: StatementHeaderProps) {
  const trpc = useTRPC();

  const {
    data: { org },
    error,
  } = useSuspenseQuery(trpc.auth.session.queryOptions());

  const { data: transactionsToday } = useSuspenseQuery(
    trpc.transaction.list.queryOptions({
      start: new Date(new Date().setHours(0, 0, 0, 0)),
      status: "settled",
    }),
  );

  // Calculate today's spend
  const _todaysSpend = transactionsToday
    ? transactionsToday.reduce((total, tx) => total + tx.amountCents, 0)
    : 0;

  // Calculate percentage used
  const percentageUsed = org
    ? Math.min(
        Math.round((org.currentBalanceCents / org.currentLimitCents) * 100),
        100,
      )
    : 0;

  if (error || !org) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <Text className="text-sm" variant="muted">
          Unable to load account information
        </Text>
      </div>
    );
  }

  // Determine progress bar color based on usage
  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-amber-500";
    return "bg-primary";
  };

  return (
    <div className="p-4">
      {/* Header with title and actions */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-bold text-2xl">Credit Limit</h2>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onOpenAutopaySettings}>
                <Settings className="mr-2 h-4 w-4" />
                Autopay Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenBankConnections}>
                <CreditCard className="mr-2 h-4 w-4" />
                Bank Connections
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={onOpenMakePayment}>
            <CreditCard className="mr-2 h-4 w-4" />
            Make a payment
          </Button>
        </div>
      </div>

      {/* Main content with balance info */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left side - Current balance */}
        <div>
          <Text className="text-sm" variant="muted">
            Current balance
          </Text>
          <Text className="font-bold text-3xl">
            {formatCurrency(org.currentBalanceCents)}
          </Text>
          <Text className="text-sm" variant="muted">
            Pending: {formatCurrency(0)}{" "}
            {/* You can add pending amount logic here */}
          </Text>
        </div>

        {/* Right side - Total limit */}
        <div className="text-left lg:text-right">
          <Text className="text-sm" variant="muted">
            Total limit
          </Text>
          <Text className="font-bold text-3xl">
            {formatCurrency(org.currentLimitCents)}
          </Text>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-6">
        <Progress
          className="h-2"
          style={
            {
              "--progress-indicator-color": getProgressColor(percentageUsed),
            } as React.CSSProperties
          }
          value={percentageUsed}
        />
      </div>

      {/* Bottom info */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {org.nextAutopayDate && (
            <div className="flex items-center">
              <Calendar className="mr-1 size-5 text-muted-foreground" />
              <Text className="text-sm" variant="muted">
                Payment Due:{" "}
                {org.nextAutopayDate.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                })}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const search = Route.useSearch();

  const { data } = useSuspenseQuery(
    trpc.transaction.listStatementPayments.queryOptions(),
  );

  const { table } = usePersistedReactTable({
    columns: tableColumns,
    data,
    filterOptions: {
      columnsConfig: [],
      strategy: "client" as const,
    },
    tableName: "statement-payments",
  });

  const closeSheet = () =>
    navigate({
      search: {
        view: undefined,
      },
      to: location.pathname,
    });

  const openMakePayment = () =>
    navigate({
      search: {
        view: "make-payment",
      },
      to: location.pathname,
    });

  const openAutopaySettings = () =>
    navigate({
      search: {
        view: "autopay-settings",
      },
      to: location.pathname,
    });

  const openBankConnections = () =>
    navigate({
      search: {
        view: "bank-connections",
      },
      to: location.pathname,
    });

  const currentTab = search.tab || "payment-history";

  const switchTab = (tab: "payment-history" | "statements") =>
    navigate({
      search: {
        ...search,
        tab,
      },
      to: location.pathname,
    });

  return (
    <Page heading="Statements And Payments">
      <Sheet
        onOpenChange={(open) => {
          if (!open) {
            closeSheet();
          }
        }}
        open={search.view !== undefined}
      >
        <SheetContent>
          {search.view === "autopay-settings" && (
            <AccountActionWrapper>
              <ConfigureAutoPay
                loadingComponent={<AccountActionsLoading />}
                onClose={() => closeSheet()}
              />
            </AccountActionWrapper>
          )}
          {search.view === "bank-connections" && (
            <AccountActionWrapper>
              <ManageBankConnections
                loadingComponent={<AccountActionsLoading />}
                onClose={() => closeSheet()}
              />
            </AccountActionWrapper>
          )}
          {search.view === "make-payment" && (
            <AccountActionWrapper>
              <MakePayment
                loadingComponent={<AccountActionsLoading />}
                onDone={() => closeSheet()}
              />
            </AccountActionWrapper>
          )}
        </SheetContent>
        <StatementHeader
          onOpenMakePayment={openMakePayment}
          onOpenAutopaySettings={openAutopaySettings}
          onOpenBankConnections={openBankConnections}
        />

        <div className="">
          <Tabs
            value={currentTab}
            onValueChange={(value) =>
              switchTab(value as "payment-history" | "statements")
            }
          >
            <div className="px-4">
              <TabsList className="mb-6" variant="standard">
                <TabsTrigger value="payment-history" variant="standard">
                  Payment History
                </TabsTrigger>
                <TabsTrigger value="statements" variant="standard">
                  Monthly Statements
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="payment-history" className="space-y-4">
              <DataTable
                table={table}
                tableName="statement-payments"
                noDataComponent="No payments found."
              />
            </TabsContent>

            <TabsContent value="statements" className="space-y-4">
              <div className="h-full max-w-3xl">
                <Statements
                  className="w-full"
                  loadingComponent={<AccountActionsLoading />}
                  onDone={() => switchTab("payment-history")}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Sheet>
    </Page>
  );
}
